import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { inr, num, dt } from '../utils/format';
import { packsOf } from '../utils/packs';
import { useAuth } from '../context/AuthContext';
import { RecipeModal } from './Recipes';

// Unit conversion (mirrors the API's utils/units.js) so ingredient cost can be
// computed even when the recipe line unit differs from the material's unit.
const TO_BASE = { kg: 1000, g: 1, l: 1000, ml: 1, piece: 1 };
const FAMILY = { kg: 'mass', g: 'mass', l: 'vol', ml: 'vol', piece: 'count' };
const convert = (qty, from, to) => {
  const q = Number(qty) || 0;
  if (!from || !to || from === to) return q;
  if (FAMILY[from] && FAMILY[from] === FAMILY[to]) return (q * TO_BASE[from]) / TO_BASE[to];
  return q;
};
const lineCostOf = (ing) => convert(ing.quantity, ing.unit, ing.rawMaterial?.unit) * Number(ing.rawMaterial?.purchasePrice || 0);

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  // Interactive costing
  const [variantId, setVariantId] = useState('');
  const [units, setUnits] = useState(100);
  const [kg, setKg] = useState(1);              // by-weight costing (products with no packs, e.g. loose)
  const [costing, setCosting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/recipes/${id}`),
      api.get(`/recipes/${id}/batches`),
    ]).then(([r, b]) => { setRecipe(r.data.recipe); setBatches(b.data.batches || []); })
      .catch(() => { toast.error('Recipe not found'); navigate('/recipes'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get('/products').then(({ data }) => setProducts(data.products || []));
    api.get('/raw-materials').then(({ data }) => setMaterials(data.materials || []));
  }, []);

  const product = products.find((p) => p.id === recipe?.productId);
  const variants = useMemo(() => packsOf(product), [product]);
  useEffect(() => { if (variants.length && !variantId) setVariantId(variants[0].id); }, [variants, variantId]);

  // No packs (loose / by-weight items) → cost a quantity in kg instead of N packs.
  const byWeight = variants.length === 0;
  const qtyUnit = recipe?.output?.unit || 'kg';   // a made raw material is costed in its own unit
  useEffect(() => {
    if (!recipe || (byWeight ? !(kg > 0) : !variantId || !units)) { setCosting(null); return; }
    const q = byWeight ? `qty=${kg}&unit=${qtyUnit}` : `units=${units}&variantId=${variantId}`;
    api.get(`/recipes/${recipe.id}/costing?${q}`)
      .then(({ data }) => setCosting(data.costing)).catch(() => setCosting(null));
  }, [recipe, variantId, units, kg, byWeight, qtyUnit]);

  if (loading) return <div className="spinner" />;
  if (!recipe) return null;

  return (
    <div className="prod-detail">
      <div className="tabs">
        <span className="tab active">Recipe / BOM</span>
        <div className="tabs-actions">
          {can('recipes:write') && <button className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>Edit</button>}
        </div>
      </div>

      <div className="prod-head">
        <div className="prod-head-main">
          <h1>{recipe.name}{!recipe.isActive && <span className="badge badge-gray" style={{ marginLeft: '.6rem' }}>Inactive</span>}</h1>
          <div className="muted">
            {recipe.product?.name || (recipe.output && <>makes raw material <Link to={`/raw-materials/${recipe.output.id}`} className="link-strong">{recipe.output.name}</Link></>)} · base yield {num(recipe.baseYieldQty)} {recipe.baseYieldUnit} · target margin {Number(recipe.targetMarginPct)}%
          </div>
          {recipe.notes && <p className="muted" style={{ marginTop: '.4rem' }}>{recipe.notes}</p>}
        </div>
      </div>

      <div className="attr-grid" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
        {/* BOM */}
        <div className="card card-pad">
          <h3 style={{ fontSize: '.95rem', marginBottom: '.6rem' }}>Ingredients (per base yield)</h3>
          <table className="grid">
            <thead><tr><th>Raw material</th><th className="right">Quantity</th><th>Unit</th><th className="right">Rate</th><th className="right">Cost</th></tr></thead>
            <tbody>
              {(recipe.ingredients || []).map((ing) => (
                <tr key={ing.id}>
                  <td><Link to={`/raw-materials/${ing.rawMaterialId}`} className="link-strong">{ing.rawMaterial?.name}</Link></td>
                  <td className="right">{num(ing.quantity)}</td><td>{ing.unit}</td>
                  <td className="right muted">{inr(ing.rawMaterial?.purchasePrice)}/{ing.rawMaterial?.unit}</td>
                  <td className="right">{inr(lineCostOf(ing))}</td>
                </tr>
              ))}
              {!(recipe.ingredients || []).length && <tr><td colSpan={5} className="empty">No ingredients.</td></tr>}
            </tbody>
            {(recipe.ingredients || []).length > 0 && (
              <tfoot><tr>
                <td colSpan={4} className="right"><strong>Material cost / base yield</strong></td>
                <td className="right"><strong>{inr((recipe.ingredients || []).reduce((s, ing) => s + lineCostOf(ing), 0))}</strong></td>
              </tr></tfoot>
            )}
          </table>
          <div className="row" style={{ marginTop: '.8rem' }}>
            <div className="field"><label>Labor / yield</label><div>{(recipe.laborPct != null && recipe.laborPct !== '') ? `${num(recipe.laborPct)}% of material` : inr(recipe.laborCost)}</div></div>
            <div className="field"><label>Packing / yield</label><div>{inr(recipe.packingCost)}</div></div>
          </div>
        </div>

        {/* Interactive costing */}
        <div className="card card-pad">
          <h3 style={{ fontSize: '.95rem', marginBottom: '.6rem' }}>Costing</h3>
          <>
              {byWeight ? (
              <div className="row">
                <div className="field"><label>Quantity ({qtyUnit})</label><input type="number" min="0" step="0.001" value={kg} onChange={(e) => setKg(Number(e.target.value) || 0)} /></div>
              </div>
              ) : (
              <div className="row">
                <div className="field"><label>Variant</label><select value={variantId} onChange={(e) => setVariantId(e.target.value)}>{variants.map((v) => <option key={v.id} value={v.id}>{v.weightLabel}</option>)}</select></div>
                <div className="field"><label>Units</label><input type="number" value={units} onChange={(e) => setUnits(Number(e.target.value) || 0)} /></div>
              </div>
              )}
              {costing ? (
                <table className="attr-table" style={{ marginTop: '.4rem' }}>
                  <tbody>
                    <tr><th>Output</th><td>{num(costing.outputKg)} {byWeight ? qtyUnit : 'kg'}{costing.units ? ` (${costing.units} units)` : ''}</td></tr>
                    <tr><th>Material cost</th><td>{inr(costing.materialCost)}</td></tr>
                    <tr><th>Labor cost</th><td>{inr(costing.laborCost)}</td></tr>
                    <tr><th>Packing cost</th><td>{inr(costing.packingCost)}</td></tr>
                    <tr><th>Total cost</th><td><strong>{inr(costing.totalCost)}</strong></td></tr>
                    <tr><th>Cost / {byWeight ? qtyUnit : 'unit'}</th><td>{inr(costing.costPerUnit)}</td></tr>
                    <tr><th>Suggested price</th><td>{inr(costing.suggestedPrice)}</td></tr>
                    <tr><th>Selling price</th><td>{costing.sellingPrice != null ? inr(costing.sellingPrice) : '—'}</td></tr>
                    <tr><th>Actual margin</th><td>{costing.actualMarginPct != null ? `${costing.actualMarginPct}%` : '—'}</td></tr>
                  </tbody>
                </table>
              ) : <div className="spinner" />}
          </>
        </div>
      </div>

      <h3 style={{ fontSize: '.95rem', margin: '1.2rem 0 .5rem' }}>Production runs ({batches.length})</h3>
      <div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Batch</th><th>Variant</th><th className="right">Target</th><th className="right">Produced</th><th className="right">Total cost</th><th className="right">Cost/unit</th><th>Status</th><th>When</th></tr></thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id}>
                <td className="mono">{b.code}</td><td>{b.variant}</td>
                <td className="right">{b.targetUnits}</td>
                <td className="right">{b.status === 'ready' ? num(b.producedQty, 0) : '—'}</td>
                <td className="right">{inr(b.totalCost)}</td>
                <td className="right">{b.costPerUnit != null ? inr(b.costPerUnit) : '—'}</td>
                <td><span className={`badge ${b.status === 'ready' ? 'badge-green' : b.status === 'cancelled' ? 'badge-red' : 'badge-gold'}`}>{b.status.replace('_', ' ')}</span></td>
                <td className="muted">{dt(b.createdAt)}</td>
              </tr>
            ))}
            {batches.length === 0 && <tr><td colSpan={8} className="empty">No production runs yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '1rem' }}><Link to="/recipes" className="btn btn-outline btn-sm">← Back to Recipes</Link></div>

      {editing && <RecipeModal recipe={recipe} products={products} materials={materials} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
};

export default RecipeDetail;
